import { Injectable, Inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { ENVIRONMENT } from '../../tokens'
import { combineLatest, EMPTY, forkJoin, from, Observable, of } from 'rxjs'
import { catchError, concatMap, delay, flatMap, map, take, tap, toArray } from 'rxjs/operators'
import * as R from 'ramda'
import * as lodash from 'lodash'
import * as momentNs from 'moment-timezone'
import { Account } from '../account/account.models'
import {
  EditRentalImageRequest,
  EnhanceImageResponse,
  UpdateRentalCustomPayload,
  UpdateRentalDetailsPayload,
  UpdateRentalInstructionsPayload,
  CreateRentalPayload,
  RentalResponse,
  UpdateSubunitRentalPayload,
  UpdateRentalPayload,
  UpdateRentalBaseRateSettingsPayload,
} from './rental.interfaces'
import { Toaster } from '../../services/toaster.service'
import {
  Rental,
  RentalBookedDayTypes,
  RentalChannelAmenityAvailableItem,
  RentalCheckTime,
  RentalMultiUnitSettings,
  RentalsImageCount,
  RentalStatus,
  TaxV3,
  TokeetImage,
  TokeetImagePayload,
} from './rental.models'
import { itemsInTimeRange, isSomething } from '../../functions'
import { Rate } from '../rate/rate.model'
import { deserializeArray } from '../../functions/deserialize-array'
import { toastError } from '../../rx-operators/toast-error'
import { PaymentScheduleType, RentalPaymentSchedule } from './schedule.model'
import { TimePickerUtils } from '../../components/time-picker'

const moment = momentNs

@Injectable()
export class RentalService {
  constructor(private http: HttpClient, private toast: Toaster, @Inject(ENVIRONMENT) private environment: any) {}

  static getTimeZone({ checkin, checkout, address }: Rental, account?: Account) {
    // TV3-1313: if no rental level checkin/out exists we should use the account level before falling back to the default.

    return (
      (checkin && checkin.timezone) ||
      (checkout && checkout.timezone) ||
      R.path(['attributes', 'checkin', 'timezone'], account) ||
      R.path(['attributes', 'checkout', 'timezone'], account) ||
      (address && address.timezone)
    )
  }

  static getCheckInTime(rental: Rental, checkInTime?: number, account?: Account): momentNs.Moment {
    // TV3-1313: if no rental level checkin/out exists we should use the account level before falling back to the default.

    const addressTimezone = R.pathOr(undefined, ['address', 'timezone'], rental)
    const rentalCheckIn: RentalCheckTime =
      rental.checkin || (account && account.attributes && account.attributes.checkin)

    const checkinTimezone = (rentalCheckIn && rentalCheckIn.timezone) || addressTimezone

    let checkin
    if (checkInTime && checkinTimezone) {
      checkin = moment.tz(checkInTime * 1000, checkinTimezone)
    } else if (rentalCheckIn && !R.isNil(rentalCheckIn.hour)) {
      checkin = moment(rentalCheckIn.hour + ':' + (rentalCheckIn.min || 0), 'H:m')
    } else {
      checkin = moment('10:0', 'H:m')
    }

    return checkin
  }

  static getCheckOutTime(rental: Rental, checkOutTime?: number, account?: Account): momentNs.Moment {
    // TV3-1313: if no rental level checkin/out exists we should use the account level before falling back to the default.

    const addressTimezone = R.pathOr(undefined, ['address', 'timezone'], rental)
    const rentalCheckout: RentalCheckTime =
      rental.checkout || (account && account.attributes && account.attributes.checkout)

    const checkoutTimezone = (rentalCheckout && rentalCheckout.timezone) || addressTimezone

    let checkout
    if (checkOutTime && checkoutTimezone) {
      checkout = moment.tz(checkOutTime * 1000, checkoutTimezone)
    } else if (rentalCheckout && !R.isNil(rentalCheckout.hour)) {
      checkout = moment(rentalCheckout.hour + ':' + (rentalCheckout.min || 0), 'H:m')
    } else {
      checkout = moment('9:0', 'H:m')
    }

    return checkout
  }

  all(less?: boolean): Observable<Rental[]> {
    const url = `@api/rental/all/${less === false ? '' : 'norates/'}?limit=2500`

    return this.http.get<RentalResponse[]>(url).pipe(deserializeArray<Rental>(Rental))
  }

  allNoRates(): Observable<Rental[]> {
    const url = '@api/rental/all/norates/?limit=2500'

    return this.http.get<RentalResponse[]>(url).pipe(deserializeArray<Rental>(Rental))
  }

  getOnlyRates() {
    const url = '@api/rental/all/onlyrates/?limit=2500'
    return this.http.get<RentalResponse[]>(url).pipe(deserializeArray<Rental>(Rental))
  }

  getOnlyRatesMerged(pkeys?: string[]) {
    const url = '@api/rental/all/onlyrates/merged/?limit=2500'
    const body = pkeys?.length ? { pkeys } : {}
    return this.http.post<any[]>(url, body).pipe(
      map((rentals) => rentals.map((rental) => ({ ...rental, rates: this.unmergeRates(rental.rates) }))),
      deserializeArray<Rental>(Rental)
    )
  }

  /**
   * Expands merged rates back to their original individual rates.
   * When backend merges rates with identical properties, it stores original rate info
   * in a `merged` array with { key, start, end } for each original rate.
   */
  private unmergeRates(rates: any[]): any[] {
    if (!rates || !Array.isArray(rates)) {
      return rates
    }

    const unmergedRates: any[] = []

    for (const rate of rates) {
      if (rate.merged && Array.isArray(rate.merged)) {
        // Expand merged rates back to individual rates
        for (const mergedEntry of rate.merged) {
          const { merged, ...rateWithoutMerged } = rate
          unmergedRates.push({
            ...rateWithoutMerged,
            key: mergedEntry.key,
            start: mergedEntry.start,
            end: mergedEntry.end,
          })
        }
      } else {
        // Rate was not merged, keep as-is
        unmergedRates.push(rate)
      }
    }

    return unmergedRates
  }

  get(id: string): Observable<Rental> {
    const url = `@api/rental/${id}`

    return this.http.get(url).pipe(
      take(1),
      map((response) => Rental.deserialize(response)),
      toastError(this.toast) as any
    )
  }

  getChannelAmenities() {
    const url = `@api/rental/amenities/all`

    return this.http.get<RentalChannelAmenityAvailableItem[]>(url)
  }

  loadRentalsImageCounts(rentals: string[]) {
    const url = `@api/rental/images/count`

    return this.http.post<RentalsImageCount[]>(url, { rentals })
  }

  loadRentalsImages(rentalIds: string[]) {
    const url = `@api/image/rentals/all`

    return this.http.post<TokeetImage[]>(url, { rental_ids: rentalIds }).pipe(
      deserializeArray<TokeetImage>(TokeetImage)
    )
  }

  add(payload: CreateRentalPayload): Observable<Rental> {
    const url = '@api/rental/'

    return this.http.post<RentalResponse>(url, payload).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Rental created successfully.`))
    )
  }

  _update(rentalId: string, payload: UpdateRentalPayload) {
    const url = `@api/rental/update/${rentalId}`

    return this.http.put<RentalResponse>(url, payload).pipe(map((response) => Rental.deserialize(response)))
  }

  update(rentalId: string, payload: UpdateRentalPayload): Observable<Rental> {
    return this._update(rentalId, payload).pipe(tap(() => this.toast.success($localize`Rental updated successfully.`)))
  }

  setRentalsParent(rentalIds: string[], parentId: string) {
    const url = `@api/rental/parent/`
    return this.http.put(url, { rental_ids: rentalIds, parent_id: parentId })
  }

  clearRentalsParent(rentalIds: string[]) {
    const url = `@api/rental/parent/remove/`
    return this.http.put(url, { rental_ids: rentalIds })
  }

  addSubunits(payload: CreateRentalPayload[]): Observable<Rental[]> {
    const url = '@api/rentals/'

    return this.http.post<RentalResponse[]>(url, payload).pipe(deserializeArray<Rental>(Rental))
  }

  setRentalsMainUnit(rentalIds: string[], mainId: string) {
    const url = `@api/rental/units/add/parent/`
    return this.http.put(url, { rental_ids: rentalIds, multi_unit_id: mainId })
  }

  clearRentalsMainUnit(rentalIds: string[]) {
    const url = `@api/rental/units/remove/parent/`
    return this.http.put(url, { rental_ids: rentalIds })
  }

  updateSubunit(rentalId: string, payload: UpdateSubunitRentalPayload): Observable<Rental> {
    return this._update(rentalId, payload)
  }

  updateStatus(rentalId: string, status: RentalStatus): Observable<Rental> {
    return this._update(rentalId, { status })
  }

  updateMultiUnitSettings(rentalId: string, settings: RentalMultiUnitSettings): Observable<Rental> {
    return this._update(rentalId, { multi_unit_settings: settings })
  }

  archiveRental(id: string): Observable<Rental> {
    const url = `@api/rental/archive/${id}`

    return this.http.put<Rental>(url, {}).pipe(
      map((response) => ({ ...response, deleted: 1 })),
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Rental archived successfully.`)),
      catchError((error) => {
        this.toast.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  unArchiveRental(id: string): Observable<Rental> {
    const url = `@api/rental/unarchive/${id}`

    return this.http.put<Rental>(url, {}).pipe(
      map((response) => ({ ...response, deleted: 0 })),
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Rental unarchived successfully.`)),
      catchError((error) => {
        this.toast.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  getArchivedRentals(): Observable<Rental[]> {
    const url = `@api/rental/all/deleted`

    return this.http.get<RentalResponse[]>(url).pipe(
      map((rentals) => R.map((r) => ({ ...r, deleted: 1 }), rentals)),
      deserializeArray<Rental>(Rental)
    )
  }

  updateGPS(rentalId: string, gps: any): Observable<Rental> {
    const url = `@api/rental/update/${rentalId}`

    return this.http.put<RentalResponse>(url, { gps: isSomething(gps) ? gps : null }).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Rental GPS location saved successfully.`))
    )
  }

  updateTaxes(rentalId: string, taxes: TaxV3[], silent = false): Observable<Rental> {
    const url = `@api/rental/update/${rentalId}`

    return this.http.put<RentalResponse>(url, { taxes }).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => {
        if (!silent) {
          this.toast.success($localize`Rental taxes updated successfully.`)
        }
      })
    )
  }

  updatePaymentSchedule(payload: RentalPaymentSchedule, rentalId: string, silent = false): Observable<Rental> {
    const url = `@api/rental/update/${rentalId}`

    // TV3-4455
    if (!payload[PaymentScheduleType.AT_BOOKING]) {
      payload[PaymentScheduleType.AT_BOOKING] = {
        percent: 0,
      }
    }

    return this.http.put<RentalResponse>(url, { payment_schedule: payload }).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => {
        if (!silent) {
          this.toast.success($localize`Rental payment schedule updated successfully.`)
        }
      })
    )
  }

  images(rentalId: string): Observable<TokeetImage[]> {
    const url = `@api/image/rental/${rentalId}`

    return this.http.get<object[]>(url).pipe(deserializeArray<TokeetImage>(TokeetImage))
  }

  getPrimaryImages(rentalIds?: string[]): Observable<TokeetImage[]> {
    const url = `@api/image/all/primary/`

    return this.http.put<{ [key: string]: object }>(url, rentalIds ? { rental_ids: rentalIds } : {}).pipe(
      map((res) => Object.values(res)),
      deserializeArray<TokeetImage>(TokeetImage)
    )
  }

  updateImagesOrder(images: { [key: string]: number }): Observable<TokeetImage[]> {
    return of(R.keys(images)).pipe(
      flatMap((imageId) => from(imageId)),
      concatMap((imageId) => {
        return this.http
          .put<object[]>(`@api/image/${imageId}`, { order: images[imageId] })
          .pipe(map((i) => TokeetImage.deserialize(i)))
      }),
      toArray(),
      map((images) => R.sortBy((i) => i.order, images))
    )
  }

  addImage(payload: TokeetImagePayload): Observable<TokeetImage> {
    const url = `@api/image`
    return this.http.post<TokeetImage>(url, payload).pipe(map((response) => TokeetImage.deserialize(response)))
  }

  uploadBase64ToCloudinary(base64DataUrl: string): Observable<any> {
    const { cloudinaryURL, cloudinaryCloudName, cloudinaryUploadPreset } = this.environment.config
    const url = `${cloudinaryURL}/${cloudinaryCloudName}/upload`
    return this.http.post(url, { file: base64DataUrl, upload_preset: cloudinaryUploadPreset, tags: 'album' })
  }

  editImage(imageId: string, data: EditRentalImageRequest): Observable<TokeetImage> {
    const url = `@api/image/${imageId}`

    return this.http.put<TokeetImage>(url, data).pipe(
      map((response) => TokeetImage.deserialize(response))
      // tap(() => this.toast.success('Rental image edited successfully.'))
    )
  }

  deleteImage(imageId: string, silent?: boolean): Observable<TokeetImage> {
    const url = `@api/image/delete/${imageId}`

    return this.http.delete<TokeetImage>(url).pipe(
      map((response) => TokeetImage.deserialize(response)),
      tap(() => !silent && this.toast.success($localize`Rental image deleted successfully.`))
    )
  }

  deleteImages(imageIds: string[]): Observable<TokeetImage[]> {
    return of(...lodash.chunk(imageIds, 10)).pipe(
      concatMap((ids) => {
        return combineLatest([...lodash.map(ids, (id) => this.deleteImage(id, true))])
      }),
      toArray(),
      map((data) => lodash.flatten(data)),
      tap(() => this.toast.success($localize`Rental images deleted successfully.`))
    )
  }

  primaryImage(rentalId: string, imageId: string): Observable<{}> {
    const url = `@api/image/primary/${rentalId}/${imageId}`

    return this.http
      .put<{}>(url, {})
      .pipe(tap(() => this.toast.success($localize`Rental image marked as primary successfully.`)))
  }

  remove(rentalId: string): Observable<string> {
    const url = `@api/rental/delete/${rentalId}`

    return this.http.delete<RentalResponse>(url).pipe(
      map((r) => r.pkey),
      tap(() => this.toast.success($localize`Rental deleted successfully.`))
    )
  }

  getSubDomain(): Observable<string> {
    const url = '@api/user/subdomain/'

    return this.http.get<{ subdomain: string }>(url).pipe(map((r) => r.subdomain))
  }

  addOwners(rental: Rental, ownerIds: string[]) {
    return of(ownerIds).pipe(
      flatMap((ownerId) => from(ownerId)),
      concatMap((ownerId) => this.http.put(`@api/rental/owner/${rental.id}/${ownerId}`, {})),
      toArray()
    )
  }

  removeOwners(rental: Rental, ownerIds: string[]) {
    return of(ownerIds).pipe(
      flatMap((ownerId) => from(ownerId)),
      concatMap((ownerId) => this.http.delete(`@api/rental/owner/${rental.id}/${ownerId}`)),
      toArray()
    )
  }

  addRentalsToOwner(ownerId: string, rentalIds: string[]) {
    return forkJoin(rentalIds.map((rentalId) => this.http.put(`@api/rental/owner/${rentalId}/${ownerId}`, {})))
  }

  removeRentalsToOwner(ownerId: string, rentalIds: string[]) {
    return forkJoin(rentalIds.map((rentalId) => this.http.delete(`@api/rental/owner/${rentalId}/${ownerId}`)))
  }

  restrictUsers(rental: Rental, userIds: string[]) {
    return of(userIds).pipe(
      flatMap((ownerId) => from(ownerId)),
      concatMap((ownerId) => this.restrictUser(rental, ownerId)),
      toArray()
    )
  }

  unrestrictUsers(rental: Rental, userIds: string[]) {
    return of(userIds).pipe(
      flatMap((ownerId) => from(ownerId)),
      concatMap((ownerId) => this.unrestrictUser(rental, ownerId)),
      toArray()
    )
  }

  restrictUser(rental: Rental, userId: string) {
    return this.http.put(`@api/rental/restrict/${rental.id}/${userId}`, {})
  }

  restrictUserAll(rentals: Rental[], userId: string) {
    return of(rentals).pipe(
      flatMap((rental) => from(rental)),
      concatMap((rental) => this.restrictUser(rental, userId)),
      toArray()
    )
  }

  unrestrictUser(rental: Rental, userId: string) {
    return this.http.delete(`@api/rental/unrestrict/${rental.id}/${userId}`)
  }

  unRestrictUserAll(rentals: Rental[], userId: string) {
    return of(rentals).pipe(
      flatMap((rental) => from(rental)),
      concatMap((rental) => this.unrestrictUser(rental, userId)),
      toArray()
    )
  }

  addTagsToRental(rental: Rental, tags: string[]) {
    const url = `@api/rental/update/${rental.id}`

    return this.http.put<RentalResponse>(url, { tags }).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Tags updated successfully`))
    )
  }

  updateCustom(rentalId: string, custom: UpdateRentalCustomPayload) {
    const url = `@api/rental/update/${rentalId}`

    return this.http.put<RentalResponse>(url, { ...custom }).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Rental updated successfully`))
    )
  }

  updateDetails(rentalId: string, payload: UpdateRentalDetailsPayload) {
    const url = `@api/rental/update/${rentalId}`
    return this.http.put<RentalResponse>(url, payload).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Rental updated successfully`))
    )
  }

  updateInstructions(rentalId: string, payload: UpdateRentalInstructionsPayload) {
    const url = `@api/rental/update/${rentalId}`

    return this.http.put<RentalResponse>(url, payload).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Rental updated successfully`))
    )
  }

  updateBaseRate(rentalId: string, payload: UpdateRentalBaseRateSettingsPayload) {
    const url = `@api/rental/update/${rentalId}`

    return this.http.put<RentalResponse>(url, payload).pipe(
      map((response) => Rental.deserialize(response)),
      tap(() => this.toast.success($localize`Rental base rate updated successfully`))
    )
  }

  updateAttributes(rental: Rental, attributes: object, message?: string): Observable<Rental> {
    const url = `@api/rental/update/${rental.id}`

    attributes = lodash.assign({}, rental.attributes, attributes)

    return this.http.put<RentalResponse>(url, { attributes }).pipe(map((response) => Rental.deserialize(response)))
  }

  copyRentalFees(srcRentalId: string, dstRentalIds: string[], resetDstRentals = true) {
    const url = `@api/bookingfees/copy/${srcRentalId}/rentals/`
    return this.http.post(url, { rentals: dstRentalIds, remove_existing: resetDstRentals })
  }

  copyRentalImages(srcRentalId: string, dstRentalIds: string[], resetDstRentals = true) {
    const url = `@api/image/copy/${srcRentalId}/rentals/`
    return this.http.post(url, { rentals: dstRentalIds, remove_existing: resetDstRentals })
  }

  hashTime(time: string, timezone: string): RentalCheckTime {
    if (!timezone || timezone.indexOf('/') < 0) {
      this.toast.error($localize`Please select a timezone default in rental settings.`)
      return null
    }

    return this.toCheckTime(time, timezone)
  }

  toCheckTime(time: string, timezone: string): RentalCheckTime {
    if (!time || !timezone) {
      return null
    }

    const m = TimePickerUtils.parseTime(time)
    const t = moment.tz(m, timezone)

    return {
      string: m.toISOString(),
      hour: m.hour(),
      min: m.minute(),
      offset_min: t.utcOffset(),
      timezone: timezone,
    }
  }

  getBlockedDates(rental: Rental) {
    return this.http.get<object[]>(`@api/calendar/rental/blocked/${rental.account}/${rental.id}`).pipe(
      map((items) => {
        const blockedDates: Record<string, RentalBookedDayTypes> = {}

        const dateFormat = 'YYYY-MM-DD'

        if (!lodash.isArray(items)) {
          return blockedDates
        }

        items.forEach((item) => {
          if (item['available'] && !item['closed']) {
            return
          }

          const fromDate = moment.utc(item['from'], dateFormat, true)
          const toDate = moment.utc(item['to'], dateFormat, true)

          if (!fromDate || !toDate || !fromDate.isValid() || !toDate.isValid()) {
            return
          }

          const range = itemsInTimeRange(fromDate, toDate)

          range.forEach((date, index) => {
            const start = index === 0
            const end = index === range.length - 1

            const status = index === 0 ? 'start' : index === range.length - 1 ? 'end' : 'full'

            const blockedDate = blockedDates[date]

            if (!blockedDate) {
              blockedDates[date] = status
              return
            }

            if ((status === 'start' && blockedDate === 'end') || (status === 'end' && blockedDate === 'start')) {
              blockedDates[date] = 'start-end'
            }

            if (status === 'full' && blockedDate !== 'full') {
              blockedDates[date] = 'full'
            }
          })
        })

        return blockedDates
      })
    )
  }

  getUniqueCategories(rates: Rate[]): string[] {
    return R.pipe(
      R.defaultTo([]),
      R.map((r: Rate) => (r.type === 'promotion' ? r.categories : r.category)),
      R.flatten,
      R.reject(R.isNil),
      R.uniq,
      R.map(R.when(R.is(Number), R.toString)),
      R.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    )(rates)
  }

  getMainImage(rental: Rental) {
    const images = (rental && rental.images) || []
    return images.find((img) => !!img.primary) || images[0] || null
  }

  updateCheckTimeForAllRentals(data: { checkin: RentalCheckTime; checkout: RentalCheckTime }) {
    const url = `@api/rentals/checkinout`
    return this.http.put(url, data)
  }

  getGuestPortalForRental(rentalId: string): Observable<any> {
    const url = `@api/website/portal/rental/${rentalId}`
    return this.http.get(url)
  }

  allWebsitesForRental(rentalId: string): Observable<any[]> {
    const url = `@api/websites/rental/${rentalId}`
    return this.http.get<any[]>(url)
  }

  enhanceImage(imageUrl: string, accountId: number): Observable<EnhanceImageResponse> {
    const url = `@api/ai/advance/images/enhance/`

    return this.http.post<EnhanceImageResponse>(url, { url: imageUrl })
  }
}
